import {
  getAccountNumberError,
  getPhoneNumberError,
  normalizePhoneNumber,
  validateRoutingNumber,
} from '@steerfi/shared';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import type { AddressVerificationResponse, StandardizedAddress } from '@/api/address';
import {
  type AddressFormData,
  AddressFormFields,
  AddressVerificationDialog,
  type AddressVerificationStatus,
} from '@/components/address';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface BusinessRegistrationFormProps {
  onBack: () => void;
}

type AchAccountType = 'checking' | 'savings';

interface FormData {
  // Personal info
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  // Business info
  businessName: string;
  // Address info
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  addressVerificationStatus: AddressVerificationStatus;
  // ACH info
  achAccountHolderName: string;
  achRoutingNumber: string;
  achAccountNumber: string;
  achAccountType: AchAccountType;
  achBankName: string;
}

interface FormErrors {
  [key: string]: string;
}

export function BusinessRegistrationForm({ onBack }: BusinessRegistrationFormProps) {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    addressVerificationStatus: 'not_checked',
    achAccountHolderName: '',
    achRoutingNumber: '',
    achAccountNumber: '',
    achAccountType: 'checking',
    achBankName: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  // Address verification state
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [addressVerificationResult, setAddressVerificationResult] =
    useState<AddressVerificationResponse | null>(null);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Handle address field changes
  const handleAddressChange = (field: keyof AddressFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Reset verification status when address changes
    if (formData.addressVerificationStatus !== 'not_checked') {
      setFormData((prev) => ({ ...prev, addressVerificationStatus: 'not_checked' }));
    }
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Handle address verification result
  const handleAddressVerificationResult = (result: AddressVerificationResponse) => {
    setAddressVerificationResult(result);
    // Show dialog if there are corrections or if verification failed
    if (result.hasCorrections || !result.verified) {
      setShowAddressDialog(true);
    }
  };

  // Handle accepting suggested address
  const handleAcceptSuggestedAddress = (suggested: StandardizedAddress) => {
    setFormData((prev) => ({
      ...prev,
      address1: suggested.streetAddress,
      address2: suggested.secondaryAddress || '',
      city: suggested.city,
      state: suggested.state,
      zipCode: suggested.zipCode,
      addressVerificationStatus: 'verified',
    }));
    setShowAddressDialog(false);
  };

  // Handle keeping original address
  const handleKeepOriginalAddress = () => {
    setFormData((prev) => ({
      ...prev,
      addressVerificationStatus: addressVerificationResult?.verified ? 'verified' : 'not_verified',
    }));
    setShowAddressDialog(false);
  };

  const validateStep1 = (): boolean => {
    const nextErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      nextErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      nextErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = 'Invalid email format';
    }
    const phoneError = getPhoneNumberError(formData.phoneNumber);
    if (phoneError) {
      nextErrors.phoneNumber = phoneError;
    }
    if (!formData.password) {
      nextErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      nextErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const nextErrors: FormErrors = {};

    if (!formData.businessName.trim()) {
      nextErrors.businessName = 'Business name is required';
    }

    // Address validation
    if (!formData.address1.trim()) {
      nextErrors.address1 = 'Street address is required';
    }
    if (!formData.city.trim()) {
      nextErrors.city = 'City is required';
    }
    if (!formData.state) {
      nextErrors.state = 'State is required';
    }
    if (!formData.zipCode.trim()) {
      nextErrors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
      nextErrors.zipCode = 'ZIP code must be 5 digits or 5+4 format';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    const nextErrors: FormErrors = {};

    if (!formData.achAccountHolderName.trim()) {
      nextErrors.achAccountHolderName = 'Account holder name is required';
    }

    // Use improved routing number validation with detailed error messages
    const routingValidation = validateRoutingNumber(formData.achRoutingNumber);
    if (!routingValidation.isValid && routingValidation.message) {
      nextErrors.achRoutingNumber = routingValidation.message;
    }

    // Use improved account number validation with detailed error messages
    const accountError = getAccountNumberError(formData.achAccountNumber);
    if (accountError) {
      nextErrors.achAccountNumber = accountError;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNextFromStep1 = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleNextFromStep2 = () => {
    if (validateStep2()) {
      // Default account holder name to business name if not set
      if (!formData.achAccountHolderName.trim()) {
        setFormData((prev) => ({
          ...prev,
          achAccountHolderName: prev.businessName,
        }));
      }
      setCurrentStep(3);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateStep3()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await fetch('/api/auth/register/business', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phoneNumber: normalizePhoneNumber(formData.phoneNumber),
          password: formData.password,
          businessName: formData.businessName.trim(),
          // Address fields
          address1: formData.address1.trim(),
          address2: formData.address2.trim() || null,
          city: formData.city.trim(),
          state: formData.state,
          zipCode: formData.zipCode,
          addressVerificationStatus: formData.addressVerificationStatus,
          // ACH fields
          achAccountHolderName: formData.achAccountHolderName.trim(),
          achRoutingNumber: formData.achRoutingNumber,
          achAccountNumber: formData.achAccountNumber,
          achAccountType: formData.achAccountType,
          achBankName: formData.achBankName.trim() || null,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Redirect to registration success page
        window.location.href = '/registration-success?registered=true';
      } else {
        if (response.status === 409) {
          const message = data.message?.toLowerCase() || '';
          if (message.includes('email') || message.includes('account')) {
            setErrors({
              email: 'An account with this email already exists. Please log in instead.',
            });
            setCurrentStep(1);
            return;
          }
        }

        // Handle field-specific errors (e.g., duplicate business name)
        if (data.errors && typeof data.errors === 'object') {
          const fieldErrors: FormErrors = {};

          // Convert array errors to string
          for (const [key, value] of Object.entries(data.errors)) {
            if (Array.isArray(value) && value.length > 0) {
              fieldErrors[key] = value[0];
            } else if (typeof value === 'string') {
              fieldErrors[key] = value;
            }
          }

          setErrors(fieldErrors);

          // If there's a business name error, go back to step 2
          if (fieldErrors.businessName) {
            setCurrentStep(2);
          }
        } else {
          setErrors({
            general:
              data.message || 'Registration failed. Please check your details and try again.',
          });
        }
      }
    } catch (_error) {
      setErrors({
        general: 'An unexpected error occurred during registration. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        <Button
          variant="ghost"
          onClick={() => {
            if (currentStep === 1) {
              onBack();
            } else {
              setCurrentStep((prev) => (prev === 3 ? 2 : 1));
            }
          }}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Business Registration</CardTitle>
            <CardDescription>
              {currentStep === 1 &&
                'Step 1 of 3: Your personal information for the primary account owner'}
              {currentStep === 2 && 'Step 2 of 3: Business information and address'}
              {currentStep === 3 && 'Step 3 of 3: Business ACH account information'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {currentStep === 1 && (
                <div className="space-y-4 animate-fade-slide-in">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">
                        First Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="firstName"
                        placeholder="Jane"
                        value={formData.firstName}
                        onChange={(event) => handleChange('firstName', event.target.value)}
                        className={errors.firstName ? 'border-destructive' : ''}
                      />
                      {errors.firstName && (
                        <p className="text-sm text-destructive">{errors.firstName}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">
                        Last Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="lastName"
                        placeholder="Doe"
                        value={formData.lastName}
                        onChange={(event) => handleChange('lastName', event.target.value)}
                        className={errors.lastName ? 'border-destructive' : ''}
                      />
                      {errors.lastName && (
                        <p className="text-sm text-destructive">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">
                      Work Email <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="jane.doe@business.com"
                      value={formData.email}
                      onChange={(event) => handleChange('email', event.target.value)}
                      className={errors.email ? 'border-destructive' : ''}
                    />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber">
                      Phone Number <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="phoneNumber"
                      type="tel"
                      placeholder="+1 234 567 8900"
                      value={formData.phoneNumber}
                      onChange={(event) => handleChange('phoneNumber', event.target.value)}
                      className={errors.phoneNumber ? 'border-destructive' : ''}
                    />
                    {errors.phoneNumber && (
                      <p className="text-sm text-destructive">{errors.phoneNumber}</p>
                    )}
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="password">
                        Password <span className="text-destructive">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={(event) => handleChange('password', event.target.value)}
                          className={errors.password ? 'border-destructive pr-10' : 'pr-10'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-sm text-destructive">{errors.password}</p>
                      )}
                      <p className="text-xs text-muted-foreground">At least 8 characters</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">
                        Confirm Password <span className="text-destructive">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={formData.confirmPassword}
                          onChange={(event) => handleChange('confirmPassword', event.target.value)}
                          className={errors.confirmPassword ? 'border-destructive pr-10' : 'pr-10'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                      )}
                    </div>
                  </div>

                  <Button type="button" className="w-full" size="lg" onClick={handleNextFromStep1}>
                    Next: Business Information
                  </Button>
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-4 animate-fade-slide-in">
                  <div className="space-y-2">
                    <Label htmlFor="businessName">
                      Business Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="businessName"
                      placeholder="Acme, Inc."
                      value={formData.businessName}
                      onChange={(event) => handleChange('businessName', event.target.value)}
                      className={errors.businessName ? 'border-destructive' : ''}
                    />
                    {errors.businessName && (
                      <p className="text-sm text-destructive">{errors.businessName}</p>
                    )}
                  </div>

                  {/* Business Address Section */}
                  <div className="pt-4 border-t">
                    <h3 className="text-lg font-medium mb-4">Business Address</h3>
                    <AddressFormFields
                      address={{
                        address1: formData.address1,
                        address2: formData.address2,
                        city: formData.city,
                        state: formData.state,
                        zipCode: formData.zipCode,
                      }}
                      errors={errors}
                      onChange={handleAddressChange}
                      onVerificationResult={handleAddressVerificationResult}
                      onVerificationStatusChange={(status) =>
                        setFormData((prev) => ({ ...prev, addressVerificationStatus: status }))
                      }
                    />
                  </div>

                  <Alert>
                    <AlertDescription>
                      We&apos;ll use this information to create your company profile and attach your
                      ACH account for payments. Your address will be verified with USPS.
                    </AlertDescription>
                  </Alert>

                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setCurrentStep(1)}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      className="flex-1"
                      size="lg"
                      onClick={handleNextFromStep2}
                    >
                      Next: ACH Details
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-4 animate-fade-slide-in">
                  <Alert>
                    <AlertDescription>
                      Your business ACH details will be encrypted using a secure key from AWS
                      Secrets Manager before being stored. Only masked information will be displayed
                      in the UI.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <Label htmlFor="achAccountHolderName">
                      Account Holder Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="achAccountHolderName"
                      placeholder={formData.businessName || 'Acme, Inc.'}
                      value={formData.achAccountHolderName}
                      onChange={(event) => handleChange('achAccountHolderName', event.target.value)}
                      className={errors.achAccountHolderName ? 'border-destructive' : ''}
                    />
                    {errors.achAccountHolderName && (
                      <p className="text-sm text-destructive">{errors.achAccountHolderName}</p>
                    )}
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="achRoutingNumber">
                        Routing Number <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="achRoutingNumber"
                        placeholder="123456789"
                        maxLength={9}
                        value={formData.achRoutingNumber}
                        onChange={(event) =>
                          handleChange('achRoutingNumber', event.target.value.replace(/\D/g, ''))
                        }
                        className={errors.achRoutingNumber ? 'border-destructive' : ''}
                      />
                      {errors.achRoutingNumber && (
                        <p className="text-sm text-destructive">{errors.achRoutingNumber}</p>
                      )}
                      <p className="text-xs text-muted-foreground">9 digits</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="achAccountNumber">
                        Account Number <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="achAccountNumber"
                        placeholder="1234567890"
                        maxLength={17}
                        value={formData.achAccountNumber}
                        onChange={(event) =>
                          handleChange('achAccountNumber', event.target.value.replace(/\D/g, ''))
                        }
                        className={errors.achAccountNumber ? 'border-destructive' : ''}
                      />
                      {errors.achAccountNumber && (
                        <p className="text-sm text-destructive">{errors.achAccountNumber}</p>
                      )}
                      <p className="text-xs text-muted-foreground">4-17 digits</p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="achAccountType">
                        Account Type <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={formData.achAccountType}
                        onValueChange={(value: AchAccountType) =>
                          handleChange('achAccountType', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select account type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="checking">Business Checking</SelectItem>
                          <SelectItem value="savings">Business Savings</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="achBankName">Bank Name (Optional)</Label>
                      <Input
                        id="achBankName"
                        placeholder="Chase Bank"
                        value={formData.achBankName}
                        onChange={(event) => handleChange('achBankName', event.target.value)}
                      />
                    </div>
                  </div>

                  {errors.general && (
                    <Alert variant="destructive">
                      <AlertDescription>{errors.general}</AlertDescription>
                    </Alert>
                  )}

                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setCurrentStep(2)}
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" size="lg" disabled={isSubmitting}>
                      {isSubmitting ? 'Creating Account...' : 'Create Business Account'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Address Verification Dialog */}
        <AddressVerificationDialog
          open={showAddressDialog}
          onOpenChange={setShowAddressDialog}
          verificationResult={addressVerificationResult}
          onAcceptSuggested={handleAcceptSuggestedAddress}
          onKeepOriginal={handleKeepOriginalAddress}
        />
      </div>
    </div>
  );
}
